import { useState, useEffect, useRef, useCallback } from 'react';
import { startCollaborationSession, connectToWebSocket } from '../services/collaboration.service';

const getToken = () => {
	const esignature = localStorage.getItem('esignature');
	return esignature ? `Bearer ${JSON.parse(esignature).token}` : null;
};

export const useFormCollaboration = (formId, onFieldChanged, flowId = null) => {
	const [isConnected, setIsConnected] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [flowIdState, setFlowIdState] = useState(flowId);
	const [error, setError] = useState(null);
	const socketRef = useRef(null);
	const formIdRef = useRef(formId);
	const userIdRef = useRef(null);
	const flowIdRef = useRef(flowId);
	const onFieldChangedRef = useRef(onFieldChanged);

	useEffect(() => {
		flowIdRef.current = flowId;
	}, [flowId]);

	useEffect(() => {
		onFieldChangedRef.current = onFieldChanged;
	}, [onFieldChanged]);

	const initializeCollaboration = useCallback(async () => {
		if (!formId || !flowId) return;

		try {
			const sessionData = await startCollaborationSession(formId, flowId);
			console.log('Session started:', sessionData);

			setSessionId(sessionData.session_id);
			formIdRef.current = sessionData.form_id;
			userIdRef.current = sessionData.user_id;

			const socket = connectToWebSocket(sessionData.websocket_url);
			socketRef.current = socket;

			socket.onopen = () => {
				setIsConnected(true);

				const message = {
					type: 'connect_session',
					formId: sessionData.form_id,
					flowId: flowIdRef.current,
					userId: sessionData.user_id
				};

				console.log('Sending connect_session with flowId:', flowIdRef.current);
				socket.send(JSON.stringify(message));
			};

			socket.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);

					if (data.type === 'connected') {
						console.log('Connected to session:', data);
					} else if (data.type === 'field_updated') {
						console.log('Field updated:', data);
					} else if (data.type === 'field_changed') {
						console.log('Field changed by another user:', data);
						if (onFieldChangedRef.current) {
							onFieldChangedRef.current(data.fieldId, data.fieldValue, data.userId, data.formId);
						}
					}
				} catch (err) {
					console.error('Error parsing message:', err);
				}
			};

			socket.onclose = () => {
				setIsConnected(false);
			};

			socket.onerror = (err) => {
				console.error('WebSocket error:', err);
				setError(err);
			};

		} catch (err) {
			console.error('Failed to initialize collaboration:', err);
			setError(err);
		}
	}, [formId, flowId]);

	useEffect(() => {
		console.log('useFormCollaboration useEffect triggered - flowId:', flowId);
		if (!flowId) {
			console.log('Skipping connection - flowId is null');
			return;
		}

		if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
			socketRef.current.close();
		}
		initializeCollaboration();

		return () => {
			if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
				socketRef.current.close();
			}
		};
	}, [formId, flowId, initializeCollaboration]);

	const sendFieldUpdate = useCallback((fieldId, fieldValue) => {
		if (socketRef.current &&
			socketRef.current.readyState === WebSocket.OPEN &&
			isConnected &&
			formIdRef.current) {
			const authToken = getToken();
			const message = JSON.stringify({
				type: 'edit_input_field',
				formId: formIdRef.current,
				fieldId,
				fieldValue,
				userId: userIdRef.current,
				token: authToken,
				sessionId
			});
			socketRef.current.send(message);
		}
	}, [isConnected, sessionId]);

	const sendCheckFlowStatus = useCallback(() => {
		if (socketRef.current &&
			socketRef.current.readyState === WebSocket.OPEN &&
			isConnected &&
			flowIdRef.current) {
			const authToken = getToken();
			const message = JSON.stringify({
				type: 'check_flow_status',
				flowId: flowIdRef.current,
				token: authToken
			});
			console.log('Sending check_flow_status:', message);
			socketRef.current.send(message);
		}
	}, [isConnected]);

	return {
		isConnected,
		sessionId,
		error,
		socket: socketRef.current,
		sendFieldUpdate,
		sendCheckFlowStatus
	};
};
